
export type tSprintRetroFeedback = {
    text: string;
    condition: string;
    value: number;
    impact: number;
}

export enum eFeedbackCondition {
    equals = 'equals', // was the sprint completion equal to the value?
    greaterThan = 'greaterThan', // was the sprint completion greater than the value?
    lessThan = 'lessThan', // was the sprint completion less than the value?
    between = 'between', // was the sprint completion between value and value+10?
    personalGreaterThan = 'personalGreaterThan', // did the user complete more than the value?
    personalBetween = 'personalBetween', // did the user complete between the value and value+2?
    personalLessThan = 'personalLessThan', // did the user complete less than the value?
    random = 'random', // random feedback that doesn't depend on the sprint completion
}

export const sprintRetroFeedback: tSprintRetroFeedback[] = [
    {
        text: "Great progress this sprint! Let's keep up the momentum.",
        condition: eFeedbackCondition.equals,
        value: 100,
        impact: 0.25,
    },
    {
        text: "We faced some challenges, but we handled them well.",
        condition: eFeedbackCondition.greaterThan,
        value: 95,
        impact: 0.2,
    },
    {
        text: "It would be great to have better communication next time.",
        condition: eFeedbackCondition.lessThan,
        value: 90,
        impact: 0.1,
    },
    {
        text: "We need more time for testing in the next sprint.",
        condition: eFeedbackCondition.between,
        value: 80,
        impact: 0,
    },
    {
        text: "A smoother sprint, but we could improve our planning phase.",
        condition: eFeedbackCondition.greaterThan,
        value: 85,
        impact: 0.1,  
    },
    {
        text: "Great job! I didn’t even notice how much coffee was consumed during this sprint. Maybe too much?",
        condition: eFeedbackCondition.greaterThan,
        value: 90,
        impact: 0.1,
    },
    {
        text: "We didn’t break anything this time! Must be a record!",
        condition: eFeedbackCondition.greaterThan,
        value: 85,
        impact: 0,
    },
    {
        text: "We’re getting faster. But maybe we should slow down before we start competing with jet engines.",
        condition: eFeedbackCondition.equals,
        value: 100,
        impact: 0.2,
    },
    {
        text: "Next sprint, let’s aim for fewer 'Oops!' and more 'Ah, that’s why it works!'",
        condition: eFeedbackCondition.lessThan,
        value: 75,
        impact: -0.1,
    },
    {
        text: "Team spirit was high! Especially after the third coffee break. Can we count those as sprint milestones?",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Good job! But next time, let’s try doing fewer tasks with just 'hope' as the methodology.",
        condition: eFeedbackCondition.between,
        value: 75,
        impact: 0,
    },
    {
        text: "The sprint went so smoothly, I almost believed the developers were wizards. Almost.",
        condition: eFeedbackCondition.greaterThan,
        value: 95,
        impact: 0.1,
    },
    {
        text: "Another sprint completed successfully. Maybe we should get a trophy, or at least a cookie for the effort.",
        condition: eFeedbackCondition.equals,
        value: 100,
        impact: 0.25,
    },
    {
        text: "We missed the mark this sprint. Let's take a closer look at the issues we left unfinished.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "We need to seriously reconsider our planning. Not quite where we hoped to be at the end of the sprint.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "This sprint didn’t quite hit the target. Maybe we need a little more focus on the priorities next time.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "We are running behind schedule. Next time, let’s try to manage time and expectations a bit better.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "The sprint results were lower than expected. We need to review our process and improve the team's efficiency.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "Not quite the success we were hoping for. Let's focus on fixing the blockers and improving task management.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "We fell short of the sprint goal. It’s time to evaluate what went wrong and course correct.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: 0,
    },
    {
        text: "A disappointing sprint with not enough focus on completing tasks. Let’s make sure to improve next time.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "It looks like we could have delivered more this sprint. We need better coordination and focus moving forward.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "This sprint didn’t meet expectations. Let’s analyze the causes and work on a better strategy for the next one.",
        condition: eFeedbackCondition.lessThan,
        value: 70,
        impact: -0.1,
    },
    {
        text: "This sprint was far from successful. We need to reevaluate our entire approach and make substantial improvements.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: -0.15,
    },
    {
        text: "We’re falling behind, and it’s becoming a serious issue. Let’s address the root causes and make drastic changes.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: -0.15,
    },
    {
        text: "A disappointing sprint with barely any progress made. It’s time to go back to the drawing board and rethink our strategy.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: -0.15,
    },
    {
        text: "This sprint was a complete letdown. We couldn’t even complete half of what was planned. Let’s refocus and replan for next time.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: -0.15,
    },
    {
        text: "This sprint was a disaster. The output didn’t even meet basic expectations. We need to seriously discuss what went wrong.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: -0.15,
    },
    {
        text: "We missed the mark by a large margin this sprint. This is far below the standard we should be delivering. Immediate action is needed.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: -0.15,
    },
    {
        text: "The sprint was far worse than anticipated. We didn’t complete nearly enough. Let’s focus on improving our processes and output.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: 0,
    },
    {
        text: "We barely scratched the surface this sprint. We’re not moving fast enough, and this needs urgent attention.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: 0,
    },
    {
        text: "This sprint was extremely underwhelming. We need to make serious changes to how we’re working and collaborating.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: 0,
    },
    {
        text: "A sprint full of setbacks and unfinished tasks. This isn’t the quality we should be delivering. Let’s reset and come up with a solid plan.",
        condition: eFeedbackCondition.lessThan,
        value: 50,
        impact: 0,
    },
    {
        text: "I’m proud of how much I accomplished this sprint. Definitely pushed myself to do more!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0.25,
    },
    {
        text: "This sprint, I got through more tasks than I expected. I’m definitely feeling like I’m on fire!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0.25,
    },
    {
        text: "I really outdid myself this time. More tasks completed than I thought possible, and I’m really pleased with my work.",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0.1,
    },
    {
        text: "I managed to knock out more than I initially planned. Honestly, I feel like I’m in a rhythm now!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0.1,
    },
    {
        text: "I surpassed my goals this sprint, and I’m feeling super productive. This is definitely one of my best sprints!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0.05,
    },
    {
        text: "I’m really happy with how much I got done this sprint. Definitely went above my usual pace.",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0,
    },
    {
        text: "I exceeded expectations and got more tasks completed than I had planned. This sprint was one for the books!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0,
    },
    {
        text: "I’ve pushed my limits this sprint. More work done than I expected, and I’m feeling great about my contributions!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0,
    },
    {
        text: "I’m really pleased with the work I put in this sprint. I completed more tasks than I had set out to do, and it feels great!",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0,
    },
    {
        text: "This sprint, I’ve definitely leveled up my productivity. Got through more tasks than expected, and I’m proud of the progress I’ve made.",
        condition: eFeedbackCondition.personalGreaterThan,
        value: 8,
        impact: 0,
    },{
        text: "It was a solid sprint. I’m happy with the work I did, but there’s definitely room for improvement next time!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "This sprint felt good overall! I got through my tasks with ease, but I think I could have gone even faster.",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "I made steady progress, and I’m happy with how things went. There’s always room for a bit more efficiency next time!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "I had a productive sprint, nothing too intense, but I kept things moving. Overall, I’m pleased with my pace.",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "It was a decent sprint. Got through what I needed to and learned a few things along the way. Happy with the result!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "It wasn’t my best sprint, but I’m definitely satisfied with the work I did. Could have been faster, but I enjoyed it!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "I managed to stay on track with my tasks, and I’m happy with my work. There’s still room for improvement, but I’m feeling good!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "This sprint was a balanced one. I got a good amount done without feeling too overwhelmed. Overall, I’m happy with how it went.",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "It was a steady sprint. I didn’t rush through anything, but I kept things moving at a good pace. Happy with the outcome!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "I’m happy with my performance this sprint. It wasn’t extraordinary, but it was a good and manageable pace. I enjoyed it!",
        condition: eFeedbackCondition.personalBetween,
        value: 6,
        impact: 0,
    },
    {
        text: "I’m disappointed with my performance this sprint. I feel like I could have contributed more.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: 0,
    },
    {
        text: "This sprint didn’t go as well as I’d hoped. I know I could have done more, and I’ll focus on improving next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: 0,
    },
    {
        text: "I didn’t meet my own expectations this sprint. I know I have the ability to do more, and I’ll make sure to improve next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: -0.1,
    },
    {
        text: "Not my best sprint. I didn’t get as much done as I wanted, but I’m determined to make up for it next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: 0,
    },
    {
        text: "I feel like I didn’t contribute enough this sprint. I definitely could have done more, and I’ll focus on improving my output.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: -0.1,
    },
    {
        text: "I didn’t perform as well as I should have this sprint. There’s room for improvement, and I’ll work harder in the next sprint.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: -0.1,
    },
    {
        text: "This sprint wasn’t up to my usual standard. I feel I could have done a lot more, and I’ll take steps to improve for next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: -0.1,
    },
    {
        text: "I didn’t complete as much as I expected this sprint. It wasn’t a great sprint, but I’m committed to doing better next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: 0,
    },
    {
        text: "I’m not satisfied with my performance this sprint. I could have done more, and I’ll work on making sure I contribute more next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: 0,
    },
    {
        text: "This sprint didn’t go as planned. I didn’t achieve what I hoped to, but I’m ready to give more effort next time.",
        condition: eFeedbackCondition.personalLessThan,
        value: 4,
        impact: 0,
    },{
        text: "No work was completed this sprint. This is clearly an issue with sprint planning, and we need to ensure we have meaningful tasks in the future.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: -0.3,
    },
    {
        text: "This sprint was a waste of time. There was no work for me to do, and that falls on the planning. We need more structured planning for the next sprint.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: -0.3,
    },
    {
        text: "Absolutely nothing got done. There was no work assigned, and it’s a clear indication that sprint planning wasn’t effective. Let’s fix that going forward.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: -0.3,
    },
    {
        text: "I wasn’t assigned any tasks this sprint. This is clearly a planning issue, as we need to have clear, actionable tasks ready for the team.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: 0,
    },
    {
        text: "No progress made. There were no tasks for me to work on, and it’s disappointing that sprint planning didn’t provide enough work for the team.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: -0.3,
    },
    {
        text: "I had no tasks this sprint. The sprint planning failed to account for enough work, and that needs to be addressed for next time.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: 0,
    },
    {
        text: "This sprint was completely unproductive. There was no work for me to do, and that’s clearly a result of poor sprint planning.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: 0,
    },
    {
        text: "I didn’t have any work assigned. This was an issue with how the sprint was planned, and it needs to be addressed for future sprints.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: -0.3,
    },
    {
        text: "There was no work to do this sprint. This is a clear case of sprint planning not providing the team with the tasks needed for progress.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: 0,
    },
    {
        text: "The sprint was a total standstill. I had no tasks to work on, and that’s a direct result of the planning process being misaligned with the team’s capacity.",
        condition: eFeedbackCondition.equals,
        value: 0,
        impact: 0,
    },
    {
        text: "Went to a concert this weekend. The band was great, but I’m pretty sure the drummer had a serious case of 'drum' anxiety!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I tried cooking this weekend. The recipe said ‘Serve immediately’—I served it and now I’m immediately regretting it.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Bought a new plant for my desk today. It’s my new ‘branch’ manager. Hope it doesn’t leaf me hanging.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I went for a jog yesterday. I didn’t run far, but I did make it to the snack aisle. Progress?",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I spent my weekend rewatching the entire series of ‘Friends’. Now I feel like I could solve all my coding problems with coffee and sarcasm.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Just tried a new coffee shop. They called it 'Java-mazing'. After my third cup, I’m starting to believe them!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I was at the store and accidentally walked into the 'Pets' section. I didn’t find any code to debug, but I did find a very cute hamster. So, it was a win!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Tried to organise my desk today. Now it looks like a tornado of sticky notes and cables, but at least it’s 'organised chaos'.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I tried a new workout this weekend. Turns out, the only thing that got a workout was my Netflix account.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Had a 'no work' weekend. But now, I’m back and ready to debug my social life just as much as my code.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I spent my weekend binge-watching 'The A-Team'. I don’t have a van or an awesome team, but I sure feel like I can solve anything with duct tape now!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I tried to get into retro gaming this weekend, but every time I turned on my console, it asked me if I wanted to play or 'insert coin.' I guess I need more coins...",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I bought a Rubik's Cube and solved it in under an hour. Just kidding, I used the internet. I mean, come on, it's the 80s, not 2023!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I had an 80s-style movie marathon. Nothing says 'productivity' like watching 'Back to the Future' and wondering why my hoverboard still hasn't arrived.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I tried to channel my inner 80s action hero this weekend. Unfortunately, my only weapon was a foam noodle and a very overcooked pizza.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Had a throwback to the 80s and listened to some 8-bit music. It didn’t solve any of my problems, but it did make me feel like I was in a classic video game.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I've not had this much fun since I broke my arm.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I attempted an 80s-inspired dance-off in the kitchen. Turns out, my only talent is dancing like I'm stuck in a 'Flashdance' music video.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I tried to make a 'Miami Vice' smoothie. It's mostly just a lot of tropical fruits, some ice, and a splash of regret.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I spent all weekend trying to figure out how to make my phone look like an old-school 80s brick. No luck so far, but I feel more 'retro' every time I try!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Spent the weekend loading games from a cassette on my ZX Spectrum. It only took 20 minutes for it to fail, so I guess I’m reliving the good ol’ days!",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "I tried to code some updates to Football Manager on my ZX Spectrum. ",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Finally managed to complete Manic Miner this weekend.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Hey hey 16k what does that get you today?",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0.1,
    },
    {
        text: "Do you want us to make a Speccy version of the software?",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0,
    },
    {
        text: "Sorry, what did you say?  I'm busy playing Viking Raiders on my phone.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0.1,
    },
    {
        text: "Saw a Commodore 64 in the charity shop this week, I bought it just so I could throw it in a skip.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: 0.1,
    },
    {
        text: "Life as a code monkey is great isn't it.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: -0.1,
    },
    {
        text: "The sad thing is that today’s computers totally abuse their memory, totally wasteful, you have to wait for the damn things to boot up, just appalling designs.",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: -0.1,
    },
    {
        text: "We're going to need to quickly set ground rules here...do robots/cyborgs count as computers?",
        condition: eFeedbackCondition.random,
        value: 0,
        impact: -0.1,
    }
];